import { useNavigate } from 'react-router-dom'

import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import type { StreamPost } from 'src/shared/schemas/stream/feed'
import { cn } from 'src/shared/utils/cn'
import { formatMessageTime } from 'src/shared/utils/dates/format-message-time'

import { PostOptionsMenu } from './post-options-menu'

interface PostHeaderProps {
  userId: string
  fullName: string
  username?: string
  profession?: Profession
  shortAddress?: string | null
  timestamp?: string
  className?: string
  post?: StreamPost
  clickable?: boolean
  onEdit?: () => void
  onDelete?: () => void
}

const formatNameForMobile = (fullName: string, username?: string): string => {
  if (!username) return fullName

  const combinedLength = fullName.length + (username?.length || 0)
  if (combinedLength <= 35) return fullName

  const names = fullName.split(' ')
  if (names.length <= 1) return fullName

  return names[0]
}

export const PostHeader = ({
  userId,
  fullName,
  username,
  profession,
  shortAddress,
  timestamp,
  post,
  clickable = true,
  onEdit,
  onDelete,
}: PostHeaderProps) => {
  const navigate = useNavigate()
  const { isMobile } = useBreakpoint()

  const handleAuthorClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (clickable && username) {
      navigate(`/${username}`)
    }
  }

  const displayName = isMobile ? formatNameForMobile(fullName, username) : fullName

  const formattedTimestamp = timestamp ? formatMessageTime(timestamp, { short: true }) : undefined
  const formattedShortAddress = shortAddress ? shortAddress : undefined

  return (
    <div className="flex items-start justify-between pl-0.5">
      <div
        className={cn(
          'flex items-center gap-3.5',
          clickable ? 'cursor-pointer' : 'cursor-default',
          isMobile && 'gap-2',
        )}
        onClick={handleAuthorClick}
      >
        <div className="rounded-full bg-white p-[3px] shadow-[0_1px_6px_1px_rgba(27,48,41,0.15)]">
          <ProfileAvatar userId={parseInt(userId)} size={isMobile ? 35 : 40} />
        </div>
        <div className="flex flex-col">
          <div className="flex items-center gap-1.5 text-base">
            <span className={cn('font-semibold', isMobile && 'text-xs')}>{displayName}</span>
            {profession && (
              <span
                className={cn(
                  'mt-px flex items-center truncate text-sm font-medium tracking-[-0.28px] text-muted',
                  isMobile && 'text-xs',
                )}
              >
                {professionMap[profession]?.label}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-sm text-muted">
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium tracking-[-0.28px] text-muted">
                {[formattedShortAddress, formattedTimestamp]
                  .filter(Boolean)
                  .map((item) => item)
                  .join(' · ')}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={cn('flex items-start gap-2', isMobile && 'gap-1')}>
        {post && <PostOptionsMenu post={post} onEdit={onEdit} onDelete={onDelete} />}
      </div>
    </div>
  )
}
